import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Globe,
  Sparkles,
  Loader2,
  Copy,
  Check,
  Trash2,
  Lock,
  ArrowRight,
  CreditCard,
  KeyRound,
} from "lucide-react";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useBilling } from "@/context/BillingContext";
import { toast } from "sonner";

export default function Settings() {
  const { user } = useAuth();
  const { billing, isPro, refresh } = useBilling();
  const [settings, setSettings] = useState(null);
  const [domain, setDomain] = useState("");
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(true);
  // Password change state
  const [currentPw, setCurrentPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [pwSaving, setPwSaving] = useState(false);
  const [pwErr, setPwErr] = useState("");
  const isOwner = user?.role === "owner";

  const load = async () => {
    try {
      const { data } = await api.get("/settings/workspace");
      setSettings(data);
      setDomain(data.custom_domain || "");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const saveDomain = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/settings/workspace/domain", { domain: domain.trim() });
      toast.success("Custom domain saved");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const removeDomain = async () => {
    if (!confirm("Remove the custom domain? Your QR codes will point back to the default short link.")) return;
    setSaving(true);
    try {
      await api.delete("/settings/workspace/domain");
      toast.success("Custom domain removed");
      setDomain("");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const copyCname = async () => {
    if (!settings) return;
    await navigator.clipboard.writeText(settings.cname_target);
    setCopied(true);
    toast.success("CNAME target copied");
    setTimeout(() => setCopied(false), 1600);
  };

  const changePassword = async (e) => {
    e.preventDefault();
    setPwErr("");
    if (newPw.length < 6) {
      setPwErr("New password must be at least 6 characters.");
      return;
    }
    if (newPw !== confirmPw) {
      setPwErr("New passwords don't match.");
      return;
    }
    if (newPw === currentPw) {
      setPwErr("New password must be different from your current one.");
      return;
    }
    setPwSaving(true);
    try {
      await api.post("/auth/change-password", {
        current_password: currentPw,
        new_password: newPw,
      });
      toast.success("Password updated");
      setCurrentPw("");
      setNewPw("");
      setConfirmPw("");
    } catch (e) {
      setPwErr(formatApiError(e));
    } finally {
      setPwSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <div className="flex items-center justify-center py-24 text-slate-400">
          <Loader2 className="w-6 h-6 animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-6">
        <div>
          <span className="text-xs font-bold uppercase tracking-[0.15em] text-blue-600">
            Workspace
          </span>
          <h1 className="text-3xl sm:text-4xl font-black tracking-tight text-slate-900 mt-2" data-testid="settings-heading">
            Settings
          </h1>
        </div>

        {/* Plan card */}
        <div className="bg-white border border-slate-200 rounded-2xl p-6 sm:p-8" data-testid="settings-plan-card">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className="flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-slate-500" />
                <h2 className="text-lg font-bold text-slate-900">Plan</h2>
                {isPro ? (
                  <span className="ml-1 px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 text-xs font-bold border border-blue-200">
                    PRO
                  </span>
                ) : (
                  <span className="ml-1 px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 text-xs font-bold">
                    FREE
                  </span>
                )}
              </div>
              <p className="text-sm text-slate-500 mt-1">
                {isPro
                  ? `Renews on ${billing?.subscription_expires_at ? new Date(billing.subscription_expires_at).toLocaleDateString() : "the next billing cycle"}.`
                  : `Free plan: ${billing?.campaign_count || 0} / ${billing?.campaign_limit ?? 3} campaigns used.`}
              </p>
            </div>
            <Link to="/pricing">
              <Button
                className={
                  isPro
                    ? "rounded-xl border border-slate-300 bg-white text-slate-900 hover:bg-slate-50"
                    : "rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-semibold"
                }
                data-testid="settings-manage-plan-btn"
              >
                {isPro ? "Manage plan" : (<><Sparkles className="w-4 h-4 mr-1" /> Upgrade to Pro</>)}
              </Button>
            </Link>
          </div>
        </div>

        {/* Password card */}
        <div className="bg-white border border-slate-200 rounded-2xl p-6 sm:p-8" data-testid="settings-password-card">
          <div className="flex items-center gap-2 mb-2">
            <KeyRound className="w-5 h-5 text-slate-500" />
            <h2 className="text-lg font-bold text-slate-900">Password</h2>
          </div>
          <p className="text-sm text-slate-500 mb-6">
            Change the password for <span className="font-semibold text-slate-700">{user?.email}</span>.
          </p>
          <form onSubmit={changePassword} className="space-y-4 max-w-md">
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1.5 block">
                Current password
              </Label>
              <Input
                data-testid="settings-current-password-input"
                type="password"
                required
                autoComplete="current-password"
                value={currentPw}
                onChange={(e) => setCurrentPw(e.target.value)}
                placeholder="Your current password"
                className="h-11 rounded-xl"
              />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1.5 block">
                New password
              </Label>
              <Input
                data-testid="settings-new-password-input"
                type="password"
                required
                autoComplete="new-password"
                value={newPw}
                onChange={(e) => setNewPw(e.target.value)}
                placeholder="At least 6 characters"
                className="h-11 rounded-xl"
              />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1.5 block">
                Confirm new password
              </Label>
              <Input
                data-testid="settings-confirm-password-input"
                type="password"
                required
                autoComplete="new-password"
                value={confirmPw}
                onChange={(e) => setConfirmPw(e.target.value)}
                placeholder="Type it again"
                className="h-11 rounded-xl"
              />
            </div>
            {pwErr && (
              <div
                className="text-sm bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg"
                data-testid="settings-password-error"
              >
                {pwErr}
              </div>
            )}
            <div className="flex justify-end">
              <Button
                type="submit"
                disabled={pwSaving || !currentPw || !newPw || !confirmPw}
                data-testid="settings-change-password-btn"
                className="rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-semibold px-5"
              >
                {pwSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Update password"}
              </Button>
            </div>
          </form>
        </div>

        {/* Custom domain card */}
        <div className="bg-white border border-slate-200 rounded-2xl p-6 sm:p-8" data-testid="settings-custom-domain-card">
          <div className="flex items-center gap-2 mb-2">
            <Globe className="w-5 h-5 text-slate-500" />
            <h2 className="text-lg font-bold text-slate-900">Custom domain</h2>
            {!isPro && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 text-[10px] font-black uppercase tracking-wider border border-amber-200 ml-1">
                <Lock className="w-3 h-3" /> PRO
              </span>
            )}
          </div>
          <p className="text-sm text-slate-500 mb-6">
            Point your own domain (e.g. <code className="font-mono">qr.acme.com</code>) so your QR codes stay on-brand.
          </p>

          {!isOwner ? (
            <div className="text-sm bg-amber-50 border border-amber-200 text-amber-800 px-3 py-2 rounded-lg">
              Only the workspace owner can change the custom domain.
            </div>
          ) : !isPro ? (
            <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50/60 p-6 text-center">
              <Lock className="w-6 h-6 text-slate-400 mx-auto" />
              <p className="mt-2 text-sm text-slate-600">
                Custom domains are available on the Pro plan.
              </p>
              <Link to="/pricing">
                <Button className="mt-4 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-semibold" data-testid="custom-domain-upgrade-cta">
                  <Sparkles className="w-4 h-4 mr-1" /> Upgrade to unlock
                </Button>
              </Link>
            </div>
          ) : (
            <form onSubmit={saveDomain} className="space-y-4">
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1.5 block">
                  Your domain
                </Label>
                <div className="flex gap-2">
                  <Input
                    data-testid="custom-domain-input"
                    value={domain}
                    onChange={(e) => setDomain(e.target.value)}
                    placeholder="qr.acme.com"
                    className="h-11 rounded-xl font-mono flex-1"
                  />
                  <Button
                    type="submit"
                    disabled={saving}
                    className="h-11 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-semibold px-5"
                    data-testid="custom-domain-save-btn"
                  >
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}
                  </Button>
                  {settings?.custom_domain && (
                    <button
                      type="button"
                      onClick={removeDomain}
                      className="h-11 w-11 rounded-xl border border-slate-300 text-slate-500 hover:text-red-600 hover:border-red-300 flex items-center justify-center transition-colors"
                      title="Remove"
                      data-testid="custom-domain-remove-btn"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>

              <div className="rounded-xl bg-slate-50 border border-slate-200 p-4">
                <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">
                  DNS setup — one CNAME record
                </div>
                <p className="text-xs text-slate-600 mb-3">
                  In your DNS provider, add a CNAME record pointing your subdomain to Qdurator:
                </p>
                <div className="grid grid-cols-3 gap-3 text-xs">
                  <div>
                    <div className="font-semibold text-slate-500 uppercase text-[10px] mb-1">Type</div>
                    <code className="block font-mono bg-white border border-slate-200 rounded-md px-2 py-1.5">
                      CNAME
                    </code>
                  </div>
                  <div>
                    <div className="font-semibold text-slate-500 uppercase text-[10px] mb-1">Host</div>
                    <code className="block font-mono bg-white border border-slate-200 rounded-md px-2 py-1.5 truncate">
                      {(domain || "qr").split(".")[0]}
                    </code>
                  </div>
                  <div>
                    <div className="font-semibold text-slate-500 uppercase text-[10px] mb-1">Value</div>
                    <div className="flex items-center gap-1">
                      <code className="block font-mono bg-white border border-slate-200 rounded-md px-2 py-1.5 truncate flex-1" data-testid="cname-target">
                        {settings?.cname_target}
                      </code>
                      <button
                        type="button"
                        onClick={copyCname}
                        className="p-1.5 rounded-md text-slate-400 hover:text-blue-600 hover:bg-blue-50 transition-colors"
                        title="Copy"
                        data-testid="cname-copy-btn"
                      >
                        {copied ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}
                      </button>
                    </div>
                  </div>
                </div>
                <p className="text-[11px] text-slate-500 mt-3">
                  DNS can take a few minutes to propagate. Once the CNAME resolves, your QR codes will use{" "}
                  <code className="font-mono">https://{domain || "your-domain"}/&lt;shortcode&gt;</code>.
                </p>
              </div>

              {settings?.custom_domain && (
                <div className="text-sm bg-emerald-50 border border-emerald-200 text-emerald-800 px-3 py-2 rounded-lg" data-testid="custom-domain-active">
                  ✅ Currently active: <code className="font-mono font-bold">{settings.custom_domain}</code>
                </div>
              )}
            </form>
          )}
        </div>
      </main>
    </div>
  );
}
